<template>
  <div id="renew2-popup-joblist-workgate">
    <div class="popup_block">
      <!--求人リスト-->
      <div class="popup_item">
        <div class="detail_block">
          <p class="txt_category">{{ getEmployName(detail) }}</p>
          <h4 class="joblist_h4">
            {{ detail ? detail.concise_title || detail.title : '' }}
          </h4>
          <div class="detail_list">
            <h5>募集職種</h5>
            <p>{{ getJobName(detail) }}</p>

            <h5>想定年収</h5>
            <pre style="white-space: pre-wrap">{{
              wgFormatSalary(detail)
            }}</pre>

            <h5>勤務地</h5>
            <p style="overflow-wrap: break-word">
              {{ wgFormatLocation(detail) }}
            </p>

            <h5 v-if="wgGetStationName(detail) !== '-'">最寄り駅</h5>
            <p
              v-if="wgGetStationName(detail) !== '-'"
              style="overflow-wrap: break-word"
            >
              {{ wgGetStationName(detail) }}
            </p>

            <h5 v-if="detail && detail.access">アクセス</h5>
            <p v-if="detail && detail.access" style="overflow-wrap: break-word">
              {{ detail.access }}
            </p>

            <h5>仕事内容</h5>
            <pre style="white-space: pre-wrap">{{
              detail ? detail.job_description || '-' : '-'
            }}</pre>
          </div>
          <div class="detail_list">
            <h5>給与・賞与</h5>
            <p style="overflow-wrap: break-word">
              {{ wgFormatSalary(detail) }}
            </p>
          </div>
          <div class="detail_list">
            <h4>企業詳細</h4>
            <h5>社名</h5>
            <p style="overflow-wrap: break-word">
              {{ detail && detail.company ? detail.company.name : '-' }}
            </p>

            <h5>本社所在地</h5>
            <p style="overflow-wrap: break-word">
              {{ formatCompanyAddress(detail) }}
            </p>
          </div>
          <div class="detail_img">
            <img
              v-for="(imgUrl, imgIndex) in getDetailImages(detail)"
              :key="imgIndex"
              :src="imgUrl"
              alt=""
            />
          </div>
        </div>
      </div>
      <div style="font-size: 11px">
        <p>
          Yagishの<a href="https://rirekisho.yagish.jp/terms" target="_blank"
            >利用規約</a
          >、<a href="https://www.yagish.jp/privacy/" target="_blank"
            >個人情報保護方針</a
          >、<a href="https://www.yagish.jp/privacy_policy/" target="_blank"
            >プライバシーポリシー</a
          >
        </p>
        <!-- 情報の渡り先は提供元ごとに違うため、開いている求人の提供元のポリシーを出す -->
        <p>
          {{ detailProviderLabel }}の<a
            :href="detailProviderPrivacyUrl"
            style="color: #04c; border-bottom: 1px solid #04c"
            target="_blank"
            rel="noopener"
            >プライバシーポリシー</a
          >
        </p>
        <p v-if="detail && detail.detail_url">
          <a
            :href="detail.detail_url"
            target="_blank"
            rel="noopener"
            style="color: #04c; border-bottom: 1px solid #04c"
            >提供元サイトで詳細を見る</a
          >
        </p>
        <p>
          <img
            src="/static/renew_img/check_icon.png"
            alt="同意する"
            style="height: 20px"
          />規約に同意して問い合わせる
        </p>
        <button
          v-if="detailApplyEnabled"
          type="button"
          id="send-workgate-detail-application"
          @click="$emit('apply', detail, 'detail')"
          class="el-button el-button--success carat_btn02"
        >
          <span>{{ detailApplyLabel }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * おすすめ求人の詳細パネル（ダイアログ Step 2）。
 *
 * WorkgateDialog から切り出した表示専用コンポーネント。
 * 取得・応募・状態遷移は親が持ち、ここは detail を描画して apply を上げるだけ。
 */
import { computed } from 'vue'
import providersMaster from '@/assets/recommend/providers.json'
import {
  wgFormatLocation,
  wgFormatSalary,
  wgGetStationName
} from '@/utils/workgate-format'

type Detail = Record<string, any> | null

interface ProviderMaster {
  label?: string
  privacy_url?: string
  apply_label?: string
}

const props = withDefaults(defineProps<{ detail?: Detail }>(), { detail: null })

defineEmits<{
  (e: 'apply', detail: Detail, source: 'detail'): void
}>()

const detailProvider = computed(
  () => (props.detail && props.detail.provider) || 'workgate'
)
const master = computed<ProviderMaster>(
  () =>
    (providersMaster as Record<string, ProviderMaster>)[detailProvider.value] ||
    {}
)

const detailProviderLabel = computed(
  () => master.value.label || detailProvider.value
)
const detailProviderPrivacyUrl = computed(() => master.value.privacy_url || '')
const detailApplyLabel = computed(
  () => master.value.apply_label || '履歴書を提出'
)

// 応募APIが止まっている提供元（本番の Carat）と応募済みでは応募ボタンを出さない
const detailApplyEnabled = computed(() => {
  const d = props.detail
  return !d || (d.apply_enabled !== false && !d.is_applied)
})

function getEmployName(item: Detail): string {
  if (!item) return '-'
  if (item.employ_name) return item.employ_name
  if (item.employ && item.employ.name) return item.employ.name
  return '-'
}

function getJobName(item: Detail): string {
  if (!item) return '-'
  if (item.job_name) return item.job_name
  if (item.job && item.job.name) return item.job.name
  return '-'
}

function formatCompanyAddress(item: Detail): string {
  if (!item) return '-'
  // Carat は flat な1本の文字列、Workgate は company.location に分かれている
  if (item.company_address) return item.company_address
  if (!item.company) return '-'
  const loc = item.company.location
  if (!loc) return '-'
  const parts = []
  if (loc.pref) parts.push(loc.pref)
  if (loc.city) parts.push(loc.city)
  if (loc.address) parts.push(loc.address)
  return parts.length > 0 ? parts.join('') : '-'
}

function getDetailImages(item: Detail): string[] {
  if (!item) return []
  const imgs: string[] = []
  if (item.image1) imgs.push(item.image1)
  if (item.image2) imgs.push(item.image2)
  return imgs
}
</script>

<style scoped>
/*
 * 親（WorkgateDialog）の scoped スタイルは子の要素に届かないため、
 * このパネルが依存する殻のルールだけ複製する。値は親と同一に保つこと
 */
#renew2-popup-joblist-workgate .popup_block {
  text-align: center;
  position: relative;
  z-index: 1;
}

#renew2-popup-joblist-workgate .popup_block .popup_item {
  background: #eef5e8;
  padding: 0 20px 20px;
  width: calc(100% + 80px);
  margin: 0 -40px;
  height: 65vh;
  overflow: scroll;
  margin-top: 10px;
  border-top: 2px solid #0f9934;
}

#renew2-popup-joblist-workgate .popup_block .popup_item .detail_block {
  padding-right: 20px;
  padding-left: 20px;
  text-align: left;
}

#renew2-popup-joblist-workgate
  .popup_block
  .popup_item
  .detail_block
  .txt_category {
  display: inline-block;
  background: rgb(15 153 52);
  color: rgb(255 255 255);
  border-radius: 50px;
  padding: 2px 10px;
  margin-right: 5px;
  font-size: 0.5rem;
}

#renew2-popup-joblist-workgate
  .popup_block
  .popup_item
  .detail_block
  .joblist_h4 {
  padding: 5px 0 0;
  font-size: 1rem;
  color: #004d40;
}

#renew2-popup-joblist-workgate
  .popup_block
  .popup_item
  .detail_block
  .detail_list {
  border-top: 1px solid #dfdfdf;
  padding-top: 20px;
  margin-top: 20px;
}

#renew2-popup-joblist-workgate
  .popup_block
  .popup_item
  .detail_block
  .detail_img {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  padding-top: 20px;
}

#renew2-popup-joblist-workgate
  .popup_block
  .popup_item
  .detail_block
  .detail_img
  img {
  width: calc(100% / 2);
  object-fit: cover;
  height: 120px;
}

#renew2-popup-joblist-workgate
  .popup_block
  .popup_item
  .detail_block
  .detail_list
  h5 {
  margin-top: 10px;
}

#renew2-popup-joblist-workgate
  .popup_block
  .popup_item
  .detail_block
  .detail_list
  h5:first-child {
  margin-top: 0;
}

#renew2-popup-joblist-workgate .popup_block .carat_btn02 {
  background-color: #e3165c;
  border-color: #e3165c;
  margin: 10px auto;
  width: 100%;
}
</style>
